import { InterceptedFormsObject, ParameterDataFormParameterType } from '../@types/form-builder.type';
import { LocationNodeData } from '../@types/live-search.type';
import { InteractionFormData, ServiceNodeAllocationData } from '../@types/service.type';

interface ProcessResult {
  success: boolean;
  error?: string;
}

interface MutationHelpers {
  updateServiceAllocation: { mutateAsync: (data: ServiceNodeAllocationData) => Promise<unknown> };
  searchAllocationNodes: { mutateAsync: (data: string) => Promise<LocationNodeData[]> };
}

interface ContainerField {
  id: number;
  type?: string;
  title?: string;
  isRequired?: boolean;
  preSelectedData?: {
    parameterType?: string;
    presetSuggestion1?: { name?: string; code?: number };
  };
}

const isSetParameterField = (field: ContainerField): boolean =>
  field.type?.toLowerCase() === 'set parameter' && !!field.preSelectedData;

const isAllocationParameter = (parameterType: string | undefined): boolean =>
  parameterType === ParameterDataFormParameterType.PrimaryAllocation ||
  parameterType === ParameterDataFormParameterType.SecondaryAllocation;

const getAllocationTypeCode = (parameterType: string | undefined): 'primary' | 'secondary' =>
  parameterType === ParameterDataFormParameterType.PrimaryAllocation ? 'primary' : 'secondary';

const getContainerFields = (
  interactions: InteractionFormData[]
): Array<{ interactionId: number; field: ContainerField }> => {
  const result: Array<{ interactionId: number; field: ContainerField }> = [];
  for (const interaction of interactions) {
    const containers = interaction.templateContent?.containers || [];
    for (const container of containers) {
      for (const field of container.fields) {
        result.push({ interactionId: interaction.id, field });
      }
    }
  }
  return result;
};

export const processSetParameterFields = async (
  interactions: InteractionFormData[],
  formValues: Record<string, unknown>,
  serviceId: number,
  helpers: MutationHelpers
): Promise<ProcessResult> => {
  const entries = getContainerFields(interactions);

  for (const { interactionId, field } of entries) {
    if (!isSetParameterField(field)) continue;

    const fieldKey = `${interactionId}-${field.id}`;
    const value = formValues[fieldKey] as Record<string, unknown> | undefined;
    const parameterType = field.preSelectedData?.parameterType;

    if (!isAllocationParameter(parameterType) || !value?.code) continue;

    try {
      await helpers.updateServiceAllocation.mutateAsync({
        serviceId,
        userDepotAllocationNodeId: value.code as unknown as number,
        allocationTypeCode: getAllocationTypeCode(parameterType),
      });
    } catch (err) {
      return {
        success: false,
        error: `Failed to update ${parameterType}: ${err instanceof Error ? err.message : 'Unknown error'}`,
      };
    }
  }
  return { success: true };
};

export const prePopulateAllocationFields = async (
  interactions: InteractionFormData[],
  searchAllocationNodes: { mutateAsync: (data: string) => Promise<LocationNodeData[]> }
): Promise<Record<string, unknown>> => {
  const initialValues: Record<string, unknown> = {};
  const entries = getContainerFields(interactions);

  for (const { interactionId, field } of entries) {
    if (!isSetParameterField(field)) continue;

    const suggestion = field.preSelectedData?.presetSuggestion1;
    if (!suggestion?.name) continue;

    try {
      const results = await searchAllocationNodes.mutateAsync(suggestion.name);
      const match = results.find((r) => r.code === String(suggestion.code));
      if (match) {
        initialValues[`${interactionId}-${field.id}`] = match;
      }
    } catch {
      // Silently skip prefill failures
    }
  }

  return initialValues;
};

/**
 * Build forms in the desktop-compatible format expected by the backend.
 * Matches the shape produced by AxiosInterceptor: templateContent spread
 * with metadata fields, and user-filled values injected into container fields.
 */
export const buildInteractionForms = (
  interactions: InteractionFormData[],
  formValues: Record<string, any>
): any[] => {
  return interactions.map((interaction) => ({
    ...interaction.templateContent,
    containers: (interaction.templateContent?.containers || []).map((container) => ({
      ...container,
      fields: container.fields.map((field) => {
        const fieldKey = `${interaction.id}-${field.id}`;
        const userValue = formValues[fieldKey];
        return userValue !== undefined ? { ...field, value: userValue } : field;
      }),
    })),
    ruleId: interaction.id,
    isMandatory: interaction.templateIsMandatory,
    isRepeatable: interaction.templateIsRepeatable,
    templateDisplayLimit: interaction.templateDisplayLimit,
    displayCount: interaction.displayCount,
    isDownloadable: interaction.templateIsDownloadable,
    isEditable: interaction.templateIsEditable,
    // Desktop AxiosInterceptor reads partActionId from raw response;
    // our InteractionFormData type omits it, so cast to access it.
    partActionId: (interaction as unknown as Record<string, unknown>).partActionId as number | undefined,
  }));
};

/**
 * Build an InterceptedFormsObject from the interaction check response,
 * matching the structure the desktop AxiosInterceptor produces.
 */
export const buildInterceptedFormsObject = (
  serviceId: number,
  nextStatus: string,
  interactions: InteractionFormData[],
  formValues: Record<string, any>,
  note: string,
  responseData: any
): InterceptedFormsObject => ({
  url: `/services/${serviceId}/status`,
  method: 'PUT',
  payload: { nextStatus, note },
  forms: buildInteractionForms(interactions, formValues),
  actionTriggers: responseData?.actionTriggers || [],
  isCheckin: responseData?.isCheckin,
  multiOnSiteSuggestionsAvailable: responseData?.multiOnSiteSuggestionsAvailable,
});

export const validateRequiredFields = (
  interactions: InteractionFormData[],
  formValues: Record<string, unknown>
): string | null => {
  const entries = getContainerFields(interactions);

  for (const { interactionId, field } of entries) {
    if (!field.isRequired) continue;
    const fieldKey = `${interactionId}-${field.id}`;
    const value = formValues[fieldKey];
    if (value === undefined || value === null || value === '') {
      return field.title ?? null;
    }
  }
  return null;
};

export const hasBlockInteraction = (interactions: InteractionFormData[]): boolean => {
  return interactions.some((interaction) =>
    interaction.templateContent?.containers?.some((container) =>
      container.fields.some((field) => field.type?.toLowerCase() === 'block')
    )
  );
};

interface BlockField {
  type?: string;
  title?: string;
  blockMessage?: string;
}

export const getBlockMessage = (interactions: InteractionFormData[]): string => {
  const entries = getContainerFields(interactions);

  for (const { field } of entries) {
    if (field.type?.toLowerCase() === 'block') {
      return (
        (field as unknown as BlockField).blockMessage || field.title || 'This action is blocked.'
      );
    }
  }
  return '';
};
